// The floating rail of the Helium layout (design/isomorphic/src/layouts/layouts.tsx,
// HeliumLayout): the logo and the grouped menu. The same content fills the drawer below xl.
// At xl and above the logo is the button that folds the rail to icons and back, as Isomorphic's
// Beryllium layout does (beryllium-left-sidebar-fixed.tsx), and the content column follows.
// Folded, the menu's scroll bar shows only while the pointer or focus is on the rail.
import { SidebarSimple } from "@phosphor-icons/react";
import { Tooltip } from "rizzui";

import { useT } from "../i18n";
import { useMode } from "../theme/mode";
import { cn } from "../ui/cn";
import { Logo, LogoMark } from "../ui/Logo";
import { RAIL_CLASS, type RailWidth } from "./railWidth";
import { SidebarMenu } from "./SidebarMenu";

const RAIL_ID = "rail";

interface Toggle {
  width: RailWidth;
  onChange: (width: RailWidth) => void;
}

function RailLogo({ compact }: { compact: boolean }) {
  const mode = useMode();
  return compact ? <LogoMark /> : <Logo tone={mode === "dark" ? "light" : "auto"} />;
}

function LogoToggle({ width, onChange }: Toggle) {
  const t = useT().shell.rail;
  const collapsed = width === "collapsed";
  const label = collapsed ? t.expandNav : t.collapseNav;
  return (
    <Tooltip content={label} placement="right">
      <button
        type="button"
        onClick={() => onChange(collapsed ? "expanded" : "collapsed")}
        aria-expanded={!collapsed}
        aria-controls={RAIL_ID}
        aria-label={label}
        className={cn(
          "group relative flex items-center rounded-xl p-1 text-start text-rail-foreground/70 transition-colors hover:bg-gray-1000/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-rail",
          !collapsed && "-ms-1 w-[calc(100%+0.5rem)]",
        )}
      >
        <RailLogo compact={collapsed} />
        {collapsed ? null : (
          <SidebarSimple
            weight="duotone"
            className="absolute end-1.5 top-1/2 h-5 w-5 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            aria-hidden
          />
        )}
      </button>
    </Tooltip>
  );
}

/** The rail's content; with `toggle` (the desktop rail) the logo folds and unfolds it. */
export function RailContent({ compact = false, onNavigate, toggle }: { compact?: boolean; onNavigate?: () => void; toggle?: Toggle }) {
  return (
    <>
      <div className={cn("flex pb-4 pt-6", compact ? "justify-center" : "px-6")}>
        {toggle ? <LogoToggle {...toggle} /> : <RailLogo compact={compact} />}
      </div>
      <div className={cn("min-h-0 flex-1 overflow-y-auto overflow-x-hidden", compact ? "rail-scrollbar" : "custom-scrollbar")}>
        <SidebarMenu compact={compact} onNavigate={onNavigate} />
      </div>
    </>
  );
}

export function Rail({ width, onWidthChange }: { width: RailWidth; onWidthChange: (width: RailWidth) => void }) {
  return (
    <aside
      id={RAIL_ID}
      className={cn("fixed bottom-0 start-0 z-50 hidden h-full p-4 transition-[width] duration-200 xl:block", RAIL_CLASS[width])}
    >
      <div className="flex h-full flex-col rounded-2xl bg-rail pb-2 shadow-float">
        <RailContent compact={width === "collapsed"} toggle={{ width, onChange: onWidthChange }} />
      </div>
    </aside>
  );
}
